import { useParams, Link, Navigate } from "react-router-dom";
import {
  Sparkles, LayoutDashboard, Users, HeartHandshake, FileSignature,
  Check, ArrowRight, Activity, Bell, ShieldCheck, Clock, PenTool, TrendingUp,
  BarChart3, Stethoscope, Ghost, Calculator, Sigma, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Reveal, PageHero, SectionHeading, ClosingCTA } from "@/components/marketing/primitives";
import { AnimatedWalkthrough } from "@/components/marketing/AnimatedWalkthrough";
import { PRODUCT_WALKTHROUGHS } from "@/components/marketing/walkthroughContent";
import { ProductScreenshot } from "@/components/marketing/ProductScreenshot";
import { MarketingFAQ } from "@/components/marketing/MarketingFAQ";

const CONTENT = {
  "deck-analytics": {
    icon: BarChart3,
    eyebrow: "Deck Analytics",
    title: "Your pitch starts a conversation.",
    accent: "Follow the signals.",
    subtitle: "Share a tracked deck, review slide-level engagement, and bring the next conversation into focus. Attention is a signal, not a promise of investment.",
    features: [
      { icon: Stethoscope, title: "Slide Doctor", body: "AI reads your per-slide drop-off alongside the slide content, points to where readers lose interest, and suggests how to tighten that slide." },
      { icon: Bell, title: "Pass or keep-me-posted", body: "Every viewer gets two one-tap buttons. A pass arrives with a reason and updates your pipeline; a keep-me-posted turns a silent view into a named lead." },
      { icon: Ghost, title: "Ghost detector", body: "Each prospect gets a responsiveness grade from views, replies, and activity, so you spend follow-up time on investors who are still engaged." },
    ],
    bullets: ["Per-slide seconds, completion, and drop-off on every link", "Watermarked viewing with downloads you can gate", "Email-gated links that turn anonymous views into named leads", "A notification the first time a viewer reaches your last slide"],
    note: "Deck analytics help you spot patterns. Use them alongside conversations and explicit investor feedback, not as proof of intent.",
  },
  "ai-updates": {
    icon: Sparkles,
    eyebrow: "AI Update Composer",
    title: "Accurate investor updates,",
    accent: "drafted in minutes.",
    subtitle: "Start with the numbers and notes you already have. AI shapes the first draft; you review, edit, and choose how each investor receives it. Nothing is sent without your approval.",
    features: [
      { icon: Sparkles, title: "Draft from your data", body: "Pull in connected or manual metrics and let TenX structure the narrative around what actually happened this month." },
      { icon: PenTool, title: "You stay in control", body: "Every draft is fully editable. Adjust the tone, trim the detail, and approve before a single investor sees it." },
      { icon: Clock, title: "Keep the rhythm", body: "Cadence reminders and reusable structure help you keep a monthly or weekly rhythm without rebuilding the update each time." },
    ],
    bullets: ["Structured highlights, lowlights, and asks", "Consistent voice across every update", "Metric snapshots embedded automatically", "A Transparent Founder streak badge once you report consistently"],
    note: "Choose a full email, a signed-in portal link, or an email-verified shared link for each update. Reader tracking is optional.",
  },
  "investor-portal": {
    icon: Users,
    eyebrow: "Investor Portal",
    title: "One transparent home",
    accent: "for every investor.",
    subtitle: "Give investors a current portal with every update, metric snapshot, and document you have shared, in one place instead of an email thread.",
    features: [
      { icon: LayoutDashboard, title: "Company health they can open", body: "Investors see the metrics and updates you have chosen to share with them, current as of your latest entry. Information rights decide what each investor can open." },
      { icon: Bell, title: "Updates that stay findable", body: "Each update reaches investors the way you choose: full email, portal link, or email-verified link. It stays in the portal afterwards." },
      { icon: Activity, title: "Engagement you choose to see", body: "When tracking is on, see opens, portal views, and comments, so you can follow up with the investors who are leaning in. Tracking is your choice per update." },
    ],
    bullets: ["Per-investor information rights", "Comment threads on every update", "Optional open and read tracking, chosen per update", "Free for every investor you invite"],
    note: "Investors see the information available to their account. Their portal is free, and founder-controlled information rights determine what is shared.",
  },
  "sweat-equity": {
    icon: HeartHandshake,
    eyebrow: "Sweat Equity",
    title: "Quantify the founder time",
    accent: "investors can't see.",
    subtitle: "Show investors the real capital behind below-market founder salaries, with vesting, benchmarks and a board-ready report they can trust.",
    features: [
      { icon: TrendingUp, title: "Market vs actual", body: "Capture each contributor's market and actual salary and TenX computes the sweat equity accrued over time." },
      { icon: HeartHandshake, title: "Vesting & role tracks", body: "Apply cliffs and vesting schedules per role, and reuse them as tracks across your team." },
      { icon: ShieldCheck, title: "Benchmarks you control", body: "Tune benchmark salary tables to your market so every figure holds up to scrutiny." },
    ],
    bullets: ["Backdated raises and history charts", "Runway framing for investors", "One-page PDF sweat-equity report", "Surfaced right inside investor updates"],
    note: "Sweat-equity reports explain contributions using your salary and vesting assumptions. They do not issue shares or replace legal or accounting advice.",
  },
  "employee-equity": {
    icon: Calculator,
    eyebrow: "Employee Equity",
    title: "Show every employee what their",
    accent: "options are really worth.",
    subtitle: "Your cap table already knows the preferences, the pool, and the 409A. TenX turns each option grant into a private page with vesting, today's estimated value, exit scenarios, break-even, and tax the employee can plan around. A free public calculator covers everyone else.",
    features: [
      { icon: Sigma, title: "Real valuation, not a guess", body: "Black-Scholes fair value plus an exit waterfall that pays liquidation preferences first and models future dilution, so the number survives a diligence question." },
      { icon: CalendarClock, title: "Exercise planner & tax", body: "ISO vs NSO, AMT exposure, ordinary vs long-term gains, cash needed on any exercise date, and the day gains turn long-term. Or a simple combined rate if you prefer." },
      { icon: Users, title: "Team overview & alerts", body: "Founders see every grant's vested value, break-even, and who has never opened their page. Employees get an email when the 409A or dilution assumptions change." },
    ],
    bullets: ["Private per-grant link; no employee account needed", "Employees adjust only scenario assumptions. The cap table stays untouched.", "Employees can ask a question from their page, and the answer lands back on it", "Vesting milestone emails at the cliff, each year mark, and the final vest", "One-page PDF equity statement to keep or share with an adviser", "Equity pages are included on Growth and Scale plans; see Pricing for current limits. The public calculator is free for everyone."],
    note: "Equity values and tax scenarios are estimates, not guaranteed outcomes or tax advice. Review the assumptions with a qualified adviser.",
  },
  "documents": {
    icon: FileSignature,
    eyebrow: "Documents & e-signatures",
    title: "Build, send and sign",
    accent: "without leaving TenX.",
    subtitle: "Draft investor documents with versioning and a clause configurator, then send for signature and countersign, all tracked in one place.",
    features: [
      { icon: FileSignature, title: "Clause configurator", body: "Assemble SAFEs, side letters and more from configurable clauses tuned to your stance." },
      { icon: PenTool, title: "Signatures & countersign", body: "Send for signature, track status, and countersign to produce a clean signed PDF copy." },
      { icon: Clock, title: "Versioning built in", body: "Every revision is saved, compared and restorable, so you always know what changed and when." },
    ],
    bullets: ["PDF and DOCX export", "Save signed copies to your vault", "Automatic signing reminders", "Full audit trail on every document"],
    note: "Templates are a starting point, not legal advice. Your counsel should review the terms and signing requirements for your jurisdiction.",
  },
};

const OTHER = [
  ["AI Updates", "/product/ai-updates"], ["Deck Analytics", "/product/deck-analytics"],
  ["Investor Portal", "/product/investor-portal"], ["Employee Equity", "/product/employee-equity"],
  ["Sweat Equity", "/product/sweat-equity"], ["Documents", "/product/documents"],
];

export default function ProductDetail() {
  const { slug } = useParams();
  const c = CONTENT[slug];
  if (!c) return <Navigate to="/product/ai-updates" replace />;

  return (
    <MarketingLayout className="mk-product">
      <PageHero eyebrow={c.eyebrow} title={c.title} accent={c.accent} subtitle={c.subtitle}>
        <Button asChild className="mk-button mk-button-orange" data-testid="product-cta"><Link to="/register">Start free <ArrowRight className="h-4 w-4" /></Link></Button>
        <Link to="#product-walkthrough" className="mk-text-link" data-testid="product-jump-tour">See the workflow <ArrowRight size={16}/></Link>
      </PageHero>

      <section id="product-walkthrough" className="mk-section mk-soft" data-testid="product-tour-section">
        <div className="mk-container">
          <SectionHeading eyebrow="The workflow, up close" title={`${c.eyebrow}. In the actual workspace.`} id="product-tour" />
          <AnimatedWalkthrough key={slug} feature={slug} idPrefix="product-tour" />
          <Link to="/tour" className="mk-text-link mt-7" data-testid="product-tour-link">See the full product tour <ArrowRight size={16} /></Link>
        </div>
      </section>

      <section className="mk-section"><div className="mk-container">
        <SectionHeading eyebrow="Built into the workflow" title="More context. Less back-and-forth." id="product-benefits" />
        <div className="mk-feature-grid">
          {c.features.map((f, i) => (
            <Reveal key={f.title} i={i} className="mk-feature">
                <f.icon /><h3 data-testid={`product-benefit-${i}`}>{f.title}</h3><p>{f.body}</p>
            </Reveal>
          ))}
        </div>
      </div></section>

      <section className="mk-section mk-soft">
        <div className="mk-container grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <SectionHeading eyebrow="Connected by design" title="The details matter. So does the bigger picture." id="product-included" />
            <ul className="mt-8 space-y-3">
              {c.bullets.map((b,i) => (
                <li key={b} data-testid={`product-included-item-${i}`} className="ff-manrope flex items-start gap-3 text-[15px] font-medium text-slate-700">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" strokeWidth={2.2} /> {b}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal i={1}>
            <ProductScreenshot step={PRODUCT_WALKTHROUGHS[slug].steps.at(-1)} idPrefix="product-detail-preview" />
            <p className="mt-5 text-xs leading-relaxed text-neutral-600" data-testid="product-privacy-note">{c.note}</p>
          </Reveal>
        </div>
      </section>

      <section className="mk-section"><div className="mk-container">
        <SectionHeading eyebrow="Keep exploring" title="One platform. The whole relationship." id="product-related" />
        <div className="grid gap-0 sm:grid-cols-2 lg:grid-cols-3">
          {OTHER.map(([label, href]) => (
            <Link key={href} to={href} aria-current={href.endsWith(slug) ? "page" : undefined} data-testid={`explore-platform-${href.split("/").pop()}`} className={`flex items-center justify-between gap-4 border-t border-neutral-300 px-4 py-6 text-sm transition-colors hover:bg-neutral-100 ${href.endsWith(slug)?"font-bold text-brand-ink":"text-neutral-700"}`}>{label}<ArrowRight size={16}/></Link>
          ))}
        </div>
      </div></section>
      <MarketingFAQ id="product-faq" items={[["Where does this fit in my plan?","Availability and limits depend on your plan. The Pricing page lists current tiers and capacities. You can start on Starter and upgrade as your workflow grows."],["Can I try the workspace before committing?","Yes. Start with a free founder account, or explore the product tour without signing up. The tour uses clearly labeled demo data."],["What should I know before sharing?",c.note]]} />
      <ClosingCTA id="product-footer" title="Try this workflow with your own numbers." description="Create a free founder workspace, add your first investors, and see it with real data. No card required." />
    </MarketingLayout>
  );
}
